import produce from 'immer';
import { uniqBy } from 'lodash';
import { handleActions } from 'redux-actions';
import { RECOMMEND_REVIEW_TYPE } from '../../../../constants/service/app/recommendReviewConstants';
import { setUncheckableTableTransferList, updateStoreWithPath } from '../../../../lib/common';
import { _LIST_PAGINATION_MODEL } from '../../app/models';
import actions from './actions';
import {
  RECOMMEND_REVIEW_LIST_SEARCH_DATA_MODEL,
  RECOMMEND_REVIEW_LIST_SEARCH_OPTION_MODEL,
  RECOMMEND_REVIEW_SAVE_MODEL,
} from './models';

/**
 *  INITIAL STATE
 */
const initialState = {
  /**
   *  추천 상품 후기 리스트
   */
  recommendReviewList: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  검색 박스
     */
    searchData: RECOMMEND_REVIEW_LIST_SEARCH_DATA_MODEL,
    /**
     *  검색 옵션
     */
    searchOption: RECOMMEND_REVIEW_LIST_SEARCH_OPTION_MODEL,
    /**
     *  리스트 pagination
     */
    pagination: _LIST_PAGINATION_MODEL,
  },

  /**
   *  추천 상품 후기 상세
   */
  recommendReviewDetail: {
    /**
     *  추천 상품 후기 정보
     */
    recommendReviewInfo: {
      /**
       *  추천 상품 후기 식별 id
       */
      recommendReviewId: null,
      /**
       *  추천인
       */
      adminName: null,
      /**
       *  추천 상품 후기 타입
       */
      type: RECOMMEND_REVIEW_TYPE.SEARCH_RECOMMEND,
      /**
       *  추천 상품 후기 생성일
       */
      createDate: null,
      /**
       *  메모
       */
      memo: null,
    },
    /**
     *  추천 상품 후기 아이템 리스트
     */
    recommendReviewItemList: [],
  },

  /**
   *  추천 상품 후기 등록/복사
   */
  recommendReviewSave: RECOMMEND_REVIEW_SAVE_MODEL,
};

/**
 *  REDUCER
 */
export default handleActions(
  {
    /**
     *  추천 상품 후기 데이터 변경하기 성공
     */
    [actions.SET_DATA]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(action.payload.path, action.payload.value, draft);
      });
    },

    /**
     *  추천 상품 후기 리스트 가져오기 성공
     */
    [actions.GET_RECOMMEND_REVIEW_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.recommendReviewList = {
          ...draft.recommendReviewList,
          ...action.payload.recommendReviewList,
        };
      });
    },

    /**
     *  추천 상품 후기 상세 요청하기 성공
     */
    [actions.GET_RECOMMEND_REVIEW_DETAIL_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.recommendReviewDetail = action.payload.recommendReviewDetail;
      });
    },

    /**
     *  추천 상품 후기 복사하기 전 필요한 데이터 가져오기 성공
     */
    [actions.PREPARE_COPY_RECOMMEND_REVIEW_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택할 상품 후기 리스트
         */
        draft.recommendReviewSave.recommendReviewItemList.itemList = {
          ...draft.recommendReviewSave.recommendReviewItemList.itemList,
          ...action.payload.recommendReviewSave.recommendReviewItemList,
        };

        /**
         *  상품 후기 정보
         */
        draft.recommendReviewSave.recommendReviewInfo = action.payload.recommendReviewSave.recommendReviewInfo;

        /**
         *  선택된 상품 후기 리스트
         */
        draft.recommendReviewSave.recommendReviewItemList.selectedItemList = {
          list: action.payload.recommendReviewSave.selectedRecommendReviewItemList,
          idList: action.payload.recommendReviewSave.selectedRecommendReviewItemList.map((item) => item.id),
        };

        /**
         *  선택할 상품 후기 아이템 중에 이미 설정된 상품 후기 아이템인 경우에는
         *  상품 후기 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendReviewSave.recommendReviewItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendReviewSave.recommendReviewItemList.itemList.list,
          selectedIdList: draft.recommendReviewSave.recommendReviewItemList.selectedItemList.idList,
        });
      });
    },

    /**
     *  추천 상품 후기 등록하기 전 필요한 데이터 가져오기
     */
    [actions.PREPARE_CREATE_RECOMMEND_REVIEW_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  상품 후기 리스트
         */
        draft.recommendReviewSave.recommendReviewItemList.itemList =
          action.payload.recommendReviewSave.recommendReviewItemList;
      });
    },

    /**
     *  추천 상품 후기에 연결할 아이템 리스트 요청하기 성공
     */
    [actions.GET_RECOMMEND_REVIEW_ITEM_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택할 상품 후기 리스트
         */
        draft.recommendReviewSave.recommendReviewItemList.itemList = {
          ...draft.recommendReviewSave.recommendReviewItemList.itemList,
          ...action.payload.recommendReviewItemList,
        };

        /**
         *  선택할 상품 후기 아이템 중에 이미 설정된 상품 후기 아이템인 경우에는
         *  상품 후기 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendReviewSave.recommendReviewItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendReviewSave.recommendReviewItemList.itemList.list,
          selectedIdList: draft.recommendReviewSave.recommendReviewItemList.selectedItemList.idList,
        });
      });
    },

    /**
     *  상품 후기 리스트 EXCEL 업로드 성공
     */
    [actions.SET_RECOMMEND_REVIEW_ITEM_LIST_EXCEL_UPLOAD_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  선택된 상품 후기 리스트
         */
        draft.recommendReviewSave.recommendReviewItemList.selectedItemList.list = action.payload.itemList;

        /**
         *  선택된 상품 후기 아이템의 중복 제거하기
         */
        draft.recommendReviewSave.recommendReviewItemList.selectedItemList.list = uniqBy(
          draft.recommendReviewSave.recommendReviewItemList.selectedItemList.list,
          'id',
        );

        draft.recommendReviewSave.recommendReviewItemList.selectedItemList.idList =
          draft.recommendReviewSave.recommendReviewItemList.selectedItemList.list.map((item) => item.id);

        /**
         *  선택할 상품 후기 아이템 중에 이미 설정된 상품 후기 아이템인 경우에는
         *  상품 후기 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.recommendReviewSave.recommendReviewItemList.itemList.list = setUncheckableTableTransferList({
          list: draft.recommendReviewSave.recommendReviewItemList.itemList.list,
          selectedIdList: draft.recommendReviewSave.recommendReviewItemList.selectedItemList.idList,
        });
      });
    },
  },
  initialState,
);
